// The player's day / 3-day / week schedule (#2691), on the shared
// SchedulingCalendar (react-big-calendar, lazy-loaded). Every agenda item is a
// block that opens its own page. A court booking the viewer OWNS can be
// dragged to propose a new time: the drop opens the move-request sheet and the
// block returns to its real time, because nothing moves until the club
// approves. Inside the club's cutoff, or for a booking someone else owns, the
// block is not draggable and its label says why.

import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { Alert, CircularProgress, Snackbar, Stack, Typography } from "@mui/material";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import type {
  SchedulingCalendarProps,
  SchedulingChange,
  SchedulingEvent,
} from "@bw/shared/calendar";
import { toWallClock } from "@bw/shared/calendar/time";
import type { ReschedulePolicy } from "@bw/shared/calendar/contract";
import type { AgendaItem } from "@bw/shared/types/generated";

import { useAllBookings } from "../../calendar/hooks/useAllBookings";
import {
  RequestMoveSheet,
  type MovableBooking,
} from "../../booking/components/RequestMoveSheet";
import {
  getReschedulePolicy,
  moveRequestKeys,
  outsideCutoff,
} from "../../booking/hooks/useMoveRequest";

type ScheduleEvent = SchedulingEvent<AgendaItem>;

const SchedulingCalendar = lazy(async () => {
  const calendarModule = await import("@bw/shared/calendar");
  return {
    default: calendarModule.SchedulingCalendar as unknown as (
      props: SchedulingCalendarProps<AgendaItem>,
    ) => React.ReactElement,
  };
});

const BOOKING_URL = /^\/book\/([^/?#]+)\/bookings\/([^/?#]+)/;

// Agenda sources onto the shared calendar's themed block kinds, so each kind
// of activity keeps a distinct color.
const SOURCE_KIND: Record<AgendaItem["key"]["source"], string> = {
  facility: "booking",
  program: "program_session",
  coaching: "queue_assignment",
  drop_in: "queue_assignment",
  queue: "queue_assignment",
  tournament: "tournament_match",
  league: "session",
  community: "session",
  membership: "staff_block",
};

/** The facility and booking a court-booking agenda item points at. */
function bookingRef(item: AgendaItem): { facilityId: string; bookingId: string } | null {
  if (item.key.source !== "facility" || item.kind !== "booking") return null;
  const match = BOOKING_URL.exec(item.action?.url ?? "");
  return match ? { facilityId: match[1], bookingId: match[2] } : null;
}

// Module-level so React Query memoizes the combined result between renders.
function combinePolicies(results: UseQueryResult<ReschedulePolicy>[]) {
  return results.map((result) => result.data);
}

function timeLabel(value: Date, timezone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    }).format(value);
  } catch {
    return "—";
  }
}

interface Pending {
  booking: MovableBooking;
  timezone: string;
  policyText: string;
  slot: { start: Date; end: Date };
  settle: () => void;
}

export function PlayerScheduleCalendar({
  items,
  view,
  date,
  displayTimezone,
  loading,
}: {
  items: AgendaItem[];
  view: "day" | "three_day" | "week";
  /** Local Date whose Y/M/D is the selected day in the display timezone. */
  date: Date;
  displayTimezone: string;
  loading: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pending, setPending] = useState<Pending | null>(null);
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  const events = useMemo<ScheduleEvent[]>(() => {
    const seen = new Set<string>();
    return items.flatMap((item) => {
      const id = `${item.key.source}:${item.key.row_id}`;
      if (seen.has(id) || !item.starts_at) return [];
      const start = new Date(item.starts_at);
      if (Number.isNaN(start.getTime())) return [];
      const parsedEnd = item.ends_at ? new Date(item.ends_at) : null;
      const end =
        parsedEnd && parsedEnd > start ? parsedEnd : new Date(start.getTime() + 3_600_000);
      seen.add(id);
      return [{ id, title: item.title, start, end, kind: SOURCE_KIND[item.key.source], data: item }];
    });
  }, [items]);

  // Ownership and the cutoff decide what may be dragged. Both come from the
  // server in batched reads: one read of the player's own bookings (owner +
  // status), then one reschedule policy per facility the player owns a
  // booking at. No per-booking requests.
  const refs = useMemo(
    () =>
      events.flatMap((event) => {
        const ref = bookingRef(event.data);
        return ref ? [ref] : [];
      }),
    [events],
  );
  const myBookings = useAllBookings(refs.length > 0);
  const bookingsById = useMemo(() => {
    const wanted = new Set(refs.map((ref) => ref.bookingId));
    return new Map(
      (myBookings.data ?? [])
        .filter((booking) => wanted.has(booking.booking_id))
        .map((booking) => [booking.booking_id, booking] as const),
    );
  }, [myBookings.data, refs]);
  const ownedFacilityIds = useMemo(
    () =>
      [
        ...new Set(
          refs
            .filter((ref) => bookingsById.get(ref.bookingId)?.user_id === user?.uid)
            .map((ref) => ref.facilityId),
        ),
      ].sort(),
    [bookingsById, refs, user?.uid],
  );
  const policyData = useQueries({
    queries: ownedFacilityIds.map((facilityId) => ({
      queryKey: moveRequestKeys.policy(facilityId),
      queryFn: () => getReschedulePolicy(facilityId),
      staleTime: 5 * 60_000,
      retry: false,
    })),
    combine: combinePolicies,
  });
  const policies = useMemo(
    () =>
      new Map(
        ownedFacilityIds.flatMap((facilityId, index) => {
          const data = policyData[index];
          return data ? [[facilityId, data] as const] : [];
        }),
      ),
    [ownedFacilityIds, policyData],
  );

  /** Why a booking block can or can't be dragged, for the viewer. */
  const moveState = useCallback(
    (event: ScheduleEvent) => {
      const ref = bookingRef(event.data);
      if (!ref || !user) return null;
      const booking = bookingsById.get(ref.bookingId);
      if (!booking) return null;
      if (booking.user_id !== user.uid) {
        return { movable: false as const, reason: "only the booker can request a move" };
      }
      const policy = policies.get(ref.facilityId);
      if (!policy || booking.status !== "confirmed") return null;
      if (!outsideCutoff(booking.start_time, policy.reschedule_request_cutoff_hours)) {
        return {
          movable: false as const,
          reason: `move requests close ${policy.reschedule_request_cutoff_hours} hours before start`,
        };
      }
      return { movable: true as const, reason: "drag to request a move", booking, policy };
    },
    [bookingsById, policies, user],
  );

  const canDrag = useCallback(
    (event: ScheduleEvent) => moveState(event)?.movable === true,
    [moveState],
  );

  const onChange = useCallback(
    (change: SchedulingChange<AgendaItem>) =>
      new Promise<void>((_resolve, reject) => {
        const state = moveState(change.event);
        if (!state?.movable) {
          reject(new Error("not movable"));
          return;
        }
        // The booking stays where it is until the club approves, so the
        // block always returns to its real slot when the sheet closes.
        setPending({
          booking: state.booking,
          timezone: change.event.data.timezone || displayTimezone,
          policyText: state.policy.policy_text,
          slot: { start: change.start, end: change.end },
          settle: () => reject(new Error("request is not a move")),
        });
      }),
    [displayTimezone, moveState],
  );

  const wallHours = events.flatMap((event) => {
    const start = toWallClock(event.start, displayTimezone);
    const end = toWallClock(event.end, displayTimezone);
    return [start.getHours(), end.getHours() + (end.getMinutes() > 0 ? 1 : 0)];
  });
  const dayStartHour = Math.max(0, Math.min(8, ...wallHours));
  const dayEndHour = Math.min(24, Math.max(22, ...wallHours));
  const label = view === "three_day" ? "3-day" : view;

  if (loading) return <CalendarLoading />;

  return (
    <>
      <Suspense fallback={<CalendarLoading />}>
        <SchedulingCalendar
          ariaLabel={`${label} schedule`}
          canDrag={canDrag}
          date={date}
          dayEndHour={dayEndHour}
          dayStartHour={dayStartHour}
          eventLabel={(event) => {
            const state = moveState(event);
            const place = event.data.facility_name ? `, ${event.data.facility_name}` : "";
            const reason = state ? `, ${state.reason}` : "";
            return `${event.title}, ${timeLabel(event.start, displayTimezone)} – ${timeLabel(event.end, displayTimezone)}${place}${reason}`;
          }}
          events={events}
          onEventDrop={onChange}
          onEventResize={onChange}
          onSelectEvent={(event) => {
            const url = event.data.action?.url;
            if (url) navigate(url);
          }}
          renderEvent={(event) => (
            <>
              {event.title}
              {event.data.facility_name ? (
                <Typography component="span" sx={{ display: "block", fontSize: "inherit", fontWeight: 400, opacity: 0.85 }}>
                  {event.data.facility_name}
                </Typography>
              ) : null}
            </>
          )}
          timezone={displayTimezone}
          view={view}
        />
      </Suspense>
      {pending ? (
        <RequestMoveSheet
          booking={pending.booking}
          initialSlot={pending.slot}
          onClose={() => {
            pending.settle();
            setPending(null);
          }}
          onSent={() => {
            pending.settle();
            setPending(null);
            setSentMessage("Move requested. Your booking stays put until the club approves.");
          }}
          open
          policyText={pending.policyText}
          timezone={pending.timezone}
        />
      ) : null}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={() => setSentMessage(null)}
        open={Boolean(sentMessage)}
      >
        <Alert onClose={() => setSentMessage(null)} severity="success" variant="filled">
          {sentMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

function CalendarLoading() {
  return (
    <Stack direction="row" spacing={1} role="status" sx={{ py: 6, justifyContent: "center" }}>
      <CircularProgress size={18} />
      <Typography color="text.secondary">Loading your calendar…</Typography>
    </Stack>
  );
}
